/**
 * Single source of truth for user-visible dates/times.
 * Display dates use short month + day + year (e.g. Apr 25, 2026). API and `<input type="date">` still use YYYY-MM-DD via `formatDateForInput` / `toDateOnlyApiParam`.
 */

/** Locale used for every formatted date/time in the app */
export const DISPLAY_DATE_LOCALE = "en-US";

/** date-fns picker format (e.g. react-datepicker) — tokens are date-fns specific. */
export const DISPLAY_DATE_FORMAT_PICKER = "MMM d, yyyy";

/** moment.js picker format (bootstrap-daterangepicker) — same visual output, different tokens. */
export const DISPLAY_DATE_FORMAT_PICKER_MOMENT = "MMM D, YYYY";

/** e.g. Apr 25, 2026 */
const DISPLAY_DATE_PARTS = { month: "short", day: "numeric", year: "numeric" };
/** e.g. Apr 25 */
const DISPLAY_DATE_PARTS_NO_YEAR = { month: "short", day: "numeric" };

/**
 * Parse a value into a Date for display. Date-only strings (YYYY-MM-DD) use local noon
 * so the calendar day matches the string in all time zones.
 * @param {string|Date|null|undefined} dateInput
 * @returns {Date|null}
 */
export function parseDisplayDate(dateInput) {
  if (dateInput == null || dateInput === "") return null;
  if (dateInput instanceof Date) {
    return Number.isNaN(dateInput.getTime()) ? null : dateInput;
  }
  const s = String(dateInput).trim();
  // Date-only API fields (YYYY-MM-DD): anchor at local noon so the calendar day is stable in every TZ.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const d = new Date(`${s}T12:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Format a date string or Date object (e.g. "Apr 25, 2026")
 * @param {string|Date} dateInput - The date to format
 * @param {object} options - Optional formatting options
 * @param {boolean} options.includeYear - Whether to include year (default: true)
 * @param {string} options.emptyLabel - When input is missing/invalid (default: "N/A")
 * @returns {string} Formatted date string or emptyLabel / "N/A"
 */
export const formatDate = (dateInput, options = {}) => {
  const { includeYear = true, emptyLabel = "N/A" } = options;

  const date = parseDisplayDate(dateInput);
  if (!date) return emptyLabel;

  try {
    if (includeYear) {
      return date.toLocaleDateString(DISPLAY_DATE_LOCALE, DISPLAY_DATE_PARTS);
    }

    return date.toLocaleDateString(DISPLAY_DATE_LOCALE, DISPLAY_DATE_PARTS_NO_YEAR);
  } catch {
    return emptyLabel;
  }
};

/**
 * Format a date for display in compact format (e.g., "Apr 25" — month + day, no year)
 * @param {string|Date} dateInput - The date to format
 * @param {object} [options] - passed to formatDate (e.g. emptyLabel)
 * @returns {string} Formatted date string
 */
export const formatDateShort = (dateInput, options = {}) => {
  return formatDate(dateInput, { ...options, includeYear: false });
};

/**
 * Format a date range for display (e.g., "Apr 1, 2026 - Apr 30, 2026")
 * @param {string|Date} startDate - Start date
 * @param {string|Date} endDate - End date
 * @returns {string} Formatted date range string
 */
export const formatDateRange = (startDate, endDate) => {
  const start = formatDate(startDate);
  const end = formatDate(endDate);
  return `${start} - ${end}`;
};

/**
 * Format a date + time (e.g., audit logs): "Apr 25, 2026, 3:45 PM"
 * @param {string|Date} dateInput
 * @returns {string}
 */
export const formatDateTime = (dateInput, options = {}) => {
  const { emptyLabel = "N/A" } = options;
  const date = parseDisplayDate(dateInput);
  if (!date) return emptyLabel;
  try {
    return date.toLocaleString(DISPLAY_DATE_LOCALE, {
      ...DISPLAY_DATE_PARTS,
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return emptyLabel;
  }
};

/**
 * Time only (e.g., timesheet "created" column): "3:45 PM"
 * @param {string|Date} dateInput
 * @returns {string}
 */
export const formatTime = (dateInput) => {
  const date = parseDisplayDate(dateInput);
  if (!date) return "";
  try {
    return date.toLocaleTimeString(DISPLAY_DATE_LOCALE, {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

/**
 * Format a calendar date for HTML `type="date"` values and API query/body date fields (YYYY-MM-DD).
 * Not for user-facing labels — use {@link formatDate} for display (e.g. Apr 25, 2026).
 * @param {string|Date} dateInput - The date to format
 * @returns {string} Date in YYYY-MM-DD format
 */
export const formatDateForInput = (dateInput) => {
  if (!dateInput) return "";

  try {
    const date = parseDisplayDate(dateInput);
    if (!date) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  } catch {
    return "";
  }
};

/**
 * Single calendar day as a date-range filter value (start and end both today, local time).
 * @returns {{ startDate: string, endDate: string }}
 */
export function getTodayDateRange() {
  const d = formatDateForInput(new Date());
  return { startDate: d, endDate: d };
}

/**
 * Calendar date for API (PATCH/create): `YYYY-MM-DD` or `null`.
 * Accepts HTML date values or ISO strings without shifting the day across time zones.
 */
export function toDateOnlyApiParam(value) {
  if (value == null || String(value).trim() === "") return null;
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return formatDateForInput(s) || null;
}

/**
 * Monday (local calendar) of the week containing `dateInput`.
 * Week = Mon–Sun (ISO-style); Monday is the first day.
 * @param {string|Date} dateInput
 * @returns {Date}
 */
export function getMondayOfWeekLocal(dateInput) {
  const d = parseDisplayDate(dateInput);
  if (!d) return new Date(NaN);
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = x.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + offset);
  return x;
}

/**
 * Date range for "this week (Mon–selected day)": Monday through `anchorDate` (inclusive), as API strings.
 * @param {string|Date} anchorDate — typically the day shown in timesheet nav (e.g. today or a past day)
 * @returns {{ startDate: string, endDate: string }}
 */
export function getWeekToDateRangeStrings(anchorDate) {
  const start = getMondayOfWeekLocal(anchorDate);
  const endStr = formatDateForInput(anchorDate);
  const startStr = formatDateForInput(start);
  return { startDate: startStr, endDate: endStr };
}

/**
 * Get relative time string (e.g., "3 days ago", "just now")
 * @param {string|Date} dateInput - The date to compare
 * @returns {string} Relative time string
 */
export const getRelativeTime = (dateInput) => {
  if (!dateInput) return "";

  try {
    const date = parseDisplayDate(dateInput);
    if (!date) return "";
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffMinutes < 1) return "just now";
    if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes > 1 ? "s" : ""} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;

    return formatDate(date);
  } catch {
    return "";
  }
};

export default {
  DISPLAY_DATE_LOCALE,
  DISPLAY_DATE_FORMAT_PICKER,
  DISPLAY_DATE_FORMAT_PICKER_MOMENT,
  parseDisplayDate,
  formatDate,
  formatDateShort,
  formatDateRange,
  formatDateTime,
  formatTime,
  formatDateForInput,
  getTodayDateRange,
  toDateOnlyApiParam,
  getRelativeTime,
  getMondayOfWeekLocal,
  getWeekToDateRangeStrings,
};
